import type { PortalSession } from "./auth";

export interface PortalStore {
  id: string;
  slug: string;
  name: string;
  partner: string;
  city: string;
  state: string;
  status: string;
  storeStatus?: string;
  profileType: string;
  profileLabel: string;
  phone: string;
  whatsapp?: string;
  contactUrl: string;
  instagramUrl: string;
  orderUrl: string;
  orderLabel: string;
  mapsUrl: string;
  serviceNote: string;
  dineInAvailable: boolean;
  pickupAvailable: boolean;
  onlineOrderAvailable: boolean;
  hours: StoreHour[];
  certificate?: {
    id?: string;
    code: string;
    status: string;
    title: string;
    sealUrl?: string;
    sealKey?: string;
    sealGeneratedAt?: string | null;
    verificationUrl?: string;
  } | null;
  users?: Array<{
    id: string;
    email: string;
    name: string;
    role: string;
    linkStatus: string;
  }>;
  role?: string;
  roleStatus?: string;
}

export interface StoreHour {
  dayOfWeek: number;
  opensAt?: string;
  closesAt?: string;
  closed?: boolean;
  note?: string;
  sortOrder?: number;
}

export interface PortalLead {
  id: string;
  type: string;
  name: string;
  phone: string;
  email: string;
  city: string;
  state: string;
  instagram: string;
  status: string;
  createdAt: string;
  message?: string;
}

export interface MeResponse {
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    status: string;
  };
  permissions: {
    admin: boolean;
    partner: boolean;
  };
  stores: PortalStore[];
}

export interface PortalUser {
  id: string;
  cognitoSub: string;
  email: string;
  name: string;
  globalRole: string;
  status: string;
  cognitoStatus: string;
  cognitoEnabled: boolean;
  inviteSentAt: string | null;
  inviteResentAt: string | null;
  lastCognitoSyncAt: string | null;
  lastInviteError: string;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  storeLink: {
    id: string;
    storeId: string;
    storeSlug: string;
    storeName: string;
    city: string;
    state: string;
    role: string;
    status: string;
  } | null;
}

export interface EmailStatusResponse {
  email: {
    sender: string;
    productionAccess: boolean;
    status: string;
    warning: string;
  };
}

export interface SealResponse {
  seal: {
    key: string;
    url: string;
    generatedAt: string | null;
    verificationUrl: string;
  };
  certificate: NonNullable<PortalStore["certificate"]>;
  store: PortalStore;
}

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

export function getAuthorizationToken(session: PortalSession) {
  return session.idToken || session.accessToken;
}

export async function apiRequest<T>(path: string, session: PortalSession, options: RequestInit = {}): Promise<T> {
  if (session.mock) throw new Error("mock_session");
  if (!API_BASE_URL) throw new Error("VITE_API_BASE_URL nao configurada.");

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${getAuthorizationToken(session)}`,
      ...(options.headers || {})
    }
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `API retornou ${response.status}`);
  }

  return response.json() as Promise<T>;
}

export const api = {
  me: (session: PortalSession) => apiRequest<MeResponse>("/me", session),
  adminLeads: (session: PortalSession) => apiRequest<{ leads: PortalLead[]; summary: unknown[] }>("/admin/leads", session),
  updateLead: (session: PortalSession, id: string, status: string) =>
    apiRequest<{ lead: PortalLead }>(`/admin/leads/${id}`, session, {
      method: "PATCH",
      body: JSON.stringify({ status })
    }),
  adminStores: (session: PortalSession) => apiRequest<{ stores: PortalStore[] }>("/admin/stores", session),
  createStore: (session: PortalSession, payload: Record<string, unknown>) =>
    apiRequest<{ store: PortalStore }>("/admin/stores", session, {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  updateAdminStore: (session: PortalSession, id: string, payload: Record<string, unknown>) =>
    apiRequest<{ store: PortalStore }>(`/admin/stores/${id}`, session, {
      method: "PATCH",
      body: JSON.stringify(payload)
    }),
  adminUsers: (session: PortalSession) => apiRequest<{ users: PortalUser[] }>("/admin/users", session),
  adminEmailStatus: (session: PortalSession) => apiRequest<EmailStatusResponse>("/admin/email-status", session),
  createAdminUser: (session: PortalSession, payload: Record<string, unknown>) =>
    apiRequest<{ user: PortalUser; warning?: string }>("/admin/users", session, {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  updateAdminUser: (session: PortalSession, id: string, payload: Record<string, unknown>) =>
    apiRequest<{ user: PortalUser; warning?: string }>(`/admin/users/${id}`, session, {
      method: "PATCH",
      body: JSON.stringify(payload)
    }),
  resendAdminUserInvite: (session: PortalSession, id: string) =>
    apiRequest<{ user: PortalUser; warning?: string }>(`/admin/users/${id}/invite`, session, {
      method: "POST"
    }),
  updateAdminUserStoreLink: (session: PortalSession, id: string, payload: Record<string, unknown>) =>
    apiRequest<{ user: PortalUser }>(`/admin/users/${id}/store-link`, session, {
      method: "PUT",
      body: JSON.stringify(payload)
    }),
  deleteAdminUserStoreLink: (session: PortalSession, id: string) =>
    apiRequest<{ user: PortalUser }>(`/admin/users/${id}/store-link`, session, {
      method: "DELETE"
    }),
  generateAdminStoreSeal: (session: PortalSession, id: string) =>
    apiRequest<SealResponse>(`/admin/stores/${id}/certificate/seal`, session, {
      method: "POST"
    }),
  revokeAdminStoreSeal: (session: PortalSession, id: string) =>
    apiRequest<{ certificate: PortalStore["certificate"]; store: PortalStore }>(`/admin/stores/${id}/certificate/seal`, session, {
      method: "DELETE"
    }),
  partnerStore: (session: PortalSession, id: string) => apiRequest<{ store: PortalStore }>(`/partner/stores/${id}/profile`, session),
  updatePartnerStore: (session: PortalSession, id: string, payload: Record<string, unknown>) =>
    apiRequest<{ store: PortalStore }>(`/partner/stores/${id}/profile`, session, {
      method: "PATCH",
      body: JSON.stringify(payload)
    }),
  updateHours: (session: PortalSession, id: string, hours: StoreHour[]) =>
    apiRequest<{ store: PortalStore }>(`/partner/stores/${id}/hours`, session, {
      method: "PUT",
      body: JSON.stringify({ hours })
    })
};
